import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { UpgradePlanModal } from 'components/Modals';
import { useCallback, useRef, useState } from 'react';
import useRegionStore from 'store/useRegionStore';
import useUserStore from 'store/useUserStore';
import { DEFAULT_REGION, Region } from 'types/Region';
import { containsWithoutSpecialChars } from 'util/containsWithoutSpecialChars';
import DefaultDropDown from './DefaultDropDown';

interface NeighborhoodFilterProps {
    lock?: boolean;
    placeholder?: string;
    region: Region;
    selectedNeighborhoods?: string[];
    onChanged?: (neighborhoods: string[]) => void;
    selectedStates?: string[];
    selectedCities?: string[];
}

const NeighborhoodFilter = ({
    lock = false,
    selectedNeighborhoods = [],
    onChanged = () => {},
    region = DEFAULT_REGION,
    placeholder = 'Neighborhood',
    selectedStates = [],
    selectedCities = [],
}: NeighborhoodFilterProps) => {
    const [showUpgradePlanModal, setShowUpgradePlanModal] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const childRef = useRef<{ closePopup: () => void }>(null);
    const { getNeighborhoods, getNonNeighborhoods } = useRegionStore();
    const { isPremiumUser } = useUserStore();

    const toggleNeighborhoodSelection = useCallback(
        (item: string) => {
            let newSelection: string[];
            if (selectedNeighborhoods.includes(item)) {
                newSelection = selectedNeighborhoods.filter((neighborhood) => neighborhood !== item);
            } else {
                newSelection = [...selectedNeighborhoods, item];
            }
            onChanged(newSelection);
        },
        [selectedNeighborhoods, onChanged],
    );

    const onSearchTermChanged = (text: string) => setSearchTerm(text);
    const onClearClicked = () => onChanged([]);
    const onShowModal = () => {
        if (childRef.current) {
            childRef.current.closePopup();
        }
        if (!isPremiumUser()) setShowUpgradePlanModal(true);
    };

    const neighborhoods = getNeighborhoods(region, selectedStates, selectedCities);
    const nonNeighborhoods = getNonNeighborhoods(region, selectedStates, selectedCities);

    // Remove any neighborhoods from nonNeighborhoods that exist in neighborhoods array
    const filteredNonNeighborhoods = nonNeighborhoods.filter((neighborhood) => !neighborhoods.includes(neighborhood));

    // Sort both lists alphabetically
    neighborhoods.sort((a, b) => a.localeCompare(b));
    filteredNonNeighborhoods.sort((a, b) => a.localeCompare(b));

    const titleContent = selectedNeighborhoods.length === 0 ? placeholder : <div className="truncate">{selectedNeighborhoods.join(', ')}</div>;

    return (
        <>
            <DefaultDropDown
                ref={childRef}
                Title={<div className="truncate">{titleContent}</div>}
                IsEmpty={selectedNeighborhoods.length === 0}
                onClearClicked={onClearClicked}
            >
                <div className="px-[10px] py-[5px]">
                    <div className="rounded-[5px] border border-my-gray border-opacity-60 flex items-center justify-between gap-[5px] px-[10px] py-[5px]">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => onSearchTermChanged(e.target.value)}
                            className="flex-grow focus:outline-none w-full"
                        />
                        <SearchOutlinedIcon />
                    </div>
                </div>
                <div className="max-h-[300px] overflow-y-scroll">
                    {neighborhoods
                        .filter((neighborhood) => containsWithoutSpecialChars(neighborhood, searchTerm))
                        .map((neighborhood, index) => (
                            <label key={index} className="flex items-center gap-2 p-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={selectedNeighborhoods.includes(neighborhood)}
                                    onChange={() => toggleNeighborhoodSelection(neighborhood)}
                                />
                                <span>{neighborhood}</span>
                            </label>
                        ))}
                    {lock &&
                        filteredNonNeighborhoods
                            .filter((neighborhood) => containsWithoutSpecialChars(neighborhood, searchTerm))
                            .map((neighborhood, index) => (
                                <label key={index} className="flex items-center gap-2 p-2 cursor-pointer opacity-50" onClick={onShowModal}>
                                    <input type="checkbox" checked={selectedNeighborhoods.includes(neighborhood)} disabled />
                                    <span>{neighborhood}</span>
                                </label>
                            ))}
                </div>
            </DefaultDropDown>

            <UpgradePlanModal visible={showUpgradePlanModal} onClose={() => setShowUpgradePlanModal(false)} />
        </>
    );
};

export default NeighborhoodFilter;
